import { trimStart } from 'lodash';

import { get, put } from 'utils/fetch';
import { getGlobal } from 'utils/global';
import { locationOrigin, makeUrl } from 'utils/url';

import { GLOBAL_ACCESS_TOKEN_KEY, SSO_VALIDATE_PATH } from './constants';
import { saveLoginRedirectFrom } from './utils/redirects';

/**
 * Base URL for the Portunus SSO server.  Should be available via an environment variable.
 * See `config/build-targets/[TARGET]/env.*`
 */
const PORTUNUS_URL = process.env.PORTUNUS_URL;

/**
 * Get the `service` parameter for Portunus, which tells Portunus where to
 * redirect back to in our application.
 * @returns {string}
 */
function currentService() {
  const baseUrl = locationOrigin();
  return `${baseUrl}${SSO_VALIDATE_PATH}`;
}

/**
 * Make a URL to a particular endpoint in the Portunus SSO service, configured with
 * the `service` param for our application.
 * @param {string} path
 * @param {object} params
 * @returns {string}
 */
function makePortunusUrl(path, params) {
  return makeUrl(PORTUNUS_URL, path, {
    service: currentService(),
    ...params,
  });
}

/**
 * Return a URL to the given endpoint within the Portunus API resource
 * space.
 * @param {string} endpoint An endpoint under the API path, e.g. `sessions` or `sessions/decode`
 * @returns {string} The URL with the params, e.g. <BASEURL>/api/v1/endpoint?params...
 */
function makePortunusApiUrl(endpoint) {
  return makeUrl(PORTUNUS_URL, `api/v1/${trimStart(endpoint, '/')}`);
}

/**
 * API fetch call to validate the given Portunus ticket-granting-ticket.
 * @param {string} ticket
 */
export function validateSSOTicket(ticket) {
  const endpoint = makePortunusUrl('serviceValidate', { ticket });
  return get(endpoint, { auth: false, headers: { Accept: 'text/xml' } });
}

/**
 * API fetch call to the current auth token (JWT) via the Portunus sessions/decode API endpoint.
 */
export function decodeAuthToken() {
  const endpoint = makePortunusApiUrl('sessions/decode');
  return get(endpoint, {
    headers: {
      Accept: 'application/vnd.api+json',
      'Content-type': 'application/vnd.api+json',
    },
  });
}

/**
 * API fetch call to refresh the current auth token (JWT).
 */
export function refreshAuthToken() {
  const endpoint = makePortunusApiUrl('sessions/renew');
  return put(endpoint, {
    headers: {
      Accept: 'application/vnd.api+json',
      'Content-type': 'application/vnd.api+json',
    },
  });
}

/**
 * Wrapper for built-in `fetch` which adds the expected Authorization header to the
 * request.  If no access token is available, a rejected Promise will be returned.
 *
 * @param {string} url - The URL to fetch from
 * @param {object} options - Standard options for fetch
 * @returns {Promise}
 */
export function authenticatedFetch(url, options) {
  const accessToken = getGlobal(GLOBAL_ACCESS_TOKEN_KEY);

  if (!accessToken) {
    return Promise.resolve({
      error: new Error(
        `Attempting to access an API that requires authentication, but no auth token is available. URL=${url}`,
      ),
    });
  }

  const authedOpts = {
    ...options,
    headers: {
      Authorization: `Bearer ${accessToken}`,
      ...options.headers,
    },
  };

  return fetch(url, authedOpts);
}

/* istanbul ignore next */
function hardRedirect(location) {
  window.location = location;
}

/**
 * Redirect (via window.location) to the given path on the Portunus server.
 * @private
 * @param {string} path - The Portunus endpoint to redirect to
 * @param {object} params - Any (optional) params to add to the query string
 * @param {function} doRedirect - A function which will do the redirect (injected for testing)
 */
export function portunusRedirect(path, params = {}, doRedirect = hardRedirect) {
  doRedirect(makePortunusUrl(path, params));
}

/**
 * Redirect to the Portunus login page. A 'tenant' will be added to the
 * query string using the specified tenant.
 * @param {string} tenant
 * @param {function(string, object)} doRedirect - To do the redirect (injected for testing)
 */
export function redirectToLogin(tenant, doRedirect = portunusRedirect) {
  console.log(`redirecting to login with tenant=${tenant}`);
  doRedirect('/login', { tenant });
}

/**
 * Redirect to the Portunus logout page. This will trigger a single-sign-out, then a redirection
 * back to our application. Upon landing back in our application, since at that time there will
 * be no auth info, the login page will in turn redirect back to Portunus to re-login.
 * @param {function(string, object)} doRedirect - To do the redirect (injected for testing)
 */
export function redirectToLogout(doRedirect = portunusRedirect) {
  saveLoginRedirectFrom(window.location);
  doRedirect('/logout');
}



// WEBPACK FOOTER //
// ./features/authentication/api.js